import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CreateProfileComponentInput,
  CreateProfileInput,
  Profile,
  ProfileComponent,
  ProfileComponentConfig,
  UpdateProfileComponentInput,
  UpdateProfileInput,
} from '@nx/domain';

export type ProfileMediaUploadKind =
  | 'avatar'
  | 'background'
  | 'background-remove'
  | 'component-image';

export interface ProfileState {
  profile: Profile | null;
  components: ProfileComponent[];
  editingComponentIds: string[];
  loading: boolean;
  saving: boolean;
  mediaUploading: ProfileMediaUploadKind | null;
  mediaUploadingComponentId: string | null;
  error: string | null;
}

const initialState: ProfileState = {
  profile: null,
  components: [],
  editingComponentIds: [],
  loading: false,
  saving: false,
  mediaUploading: null,
  mediaUploadingComponentId: null,
  error: null,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    profileLoadRequested(state) {
      state.loading = true;
      state.error = null;
    },
    profileLoaded(state, action: PayloadAction<Profile | null>) {
      state.profile = action.payload;
      state.loading = false;
      if (!action.payload) {
        state.components = [];
      }
    },
    profileCreateRequested(state, _action: PayloadAction<CreateProfileInput>) {
      state.saving = true;
      state.error = null;
    },
    profileSaveRequested(state, _action: PayloadAction<UpdateProfileInput>) {
      state.saving = true;
      state.error = null;
    },
    profilePublishRequested(state) {
      state.saving = true;
      state.error = null;
    },
    profileUnpublishRequested(state) {
      state.saving = true;
      state.error = null;
    },
    profileSaveSucceeded(state, action: PayloadAction<Profile>) {
      state.profile = action.payload;
      state.saving = false;
    },
    profilePatch(state, action: PayloadAction<Partial<Profile>>) {
      if (state.profile) {
        state.profile = { ...state.profile, ...action.payload };
      }
    },
    profileThemePatch(state, action: PayloadAction<Partial<Profile['themeConfig']>>) {
      if (state.profile) {
        state.profile = {
          ...state.profile,
          themeConfig: { ...state.profile.themeConfig, ...action.payload },
        };
      }
    },
    componentsLoaded(state, action: PayloadAction<ProfileComponent[]>) {
      state.components = action.payload.map((incoming) => {
        if (!state.editingComponentIds.includes(incoming.id)) {
          return incoming;
        }
        const local = state.components.find((c) => c.id === incoming.id);
        if (!local) return incoming;
        return { ...incoming, config: local.config };
      });
    },
    componentEditStarted(state, action: PayloadAction<string>) {
      if (!state.editingComponentIds.includes(action.payload)) {
        state.editingComponentIds.push(action.payload);
      }
    },
    componentEditEnded(state, action: PayloadAction<string>) {
      state.editingComponentIds = state.editingComponentIds.filter((id) => id !== action.payload);
    },
    profileComponentsWatchRequested(_state, _action: PayloadAction<string | null>) {},
    componentAddRequested(state, _action: PayloadAction<CreateProfileComponentInput>) {
      state.saving = true;
    },
    componentUpdateRequested(
      state,
      _action: PayloadAction<{ componentId: string; input: UpdateProfileComponentInput }>,
    ) {
      state.saving = true;
    },
    componentRemoveRequested(state, _action: PayloadAction<string>) {
      state.saving = true;
    },
    componentReorderRequested(state, _action: PayloadAction<string[]>) {
      state.saving = true;
    },
    componentMutationFinished(state) {
      state.saving = false;
    },
    componentConfigPatched(
      state,
      action: PayloadAction<{ componentId: string; config: Partial<ProfileComponentConfig> }>,
    ) {
      const component = state.components.find((c) => c.id === action.payload.componentId);
      if (!component) return;

      const next: ProfileComponentConfig = { ...component.config };
      const patch = action.payload.config;
      if (patch.platform !== undefined) {
        next.platform = patch.platform;
      }
      if (patch.url !== undefined) {
        if (patch.url === '') delete next.url;
        else next.url = patch.url;
      }
      if (patch.label !== undefined) {
        if (patch.label === '') delete next.label;
        else next.label = patch.label;
      }
      if (patch.content !== undefined) {
        if (patch.content === '') delete next.content;
        else next.content = patch.content;
      }
      if (patch.imageUrl !== undefined) {
        if (patch.imageUrl === '') delete next.imageUrl;
        else next.imageUrl = patch.imageUrl;
      }
      if (patch.embedId !== undefined) {
        if (patch.embedId === '') delete next.embedId;
        else next.embedId = patch.embedId;
      }
      if (patch.handle !== undefined) {
        if (patch.handle === '') delete next.handle;
        else next.handle = patch.handle;
      }
      if (patch.note !== undefined) {
        if (patch.note === '') delete next.note;
        else next.note = patch.note;
      }
      if (patch.caption !== undefined) {
        if (patch.caption === '') delete next.caption;
        else next.caption = patch.caption;
      }
      component.config = next;
    },
    profileAvatarUploadRequested(state, _action: PayloadAction<{ file: File }>) {
      state.mediaUploading = 'avatar';
      state.mediaUploadingComponentId = null;
      state.error = null;
    },
    profileBackgroundUploadRequested(state, _action: PayloadAction<{ file: File }>) {
      state.mediaUploading = 'background';
      state.mediaUploadingComponentId = null;
      state.error = null;
    },
    profileBackgroundRemoveRequested(state) {
      state.mediaUploading = 'background-remove';
      state.mediaUploadingComponentId = null;
      state.error = null;
    },
    profileComponentImageUploadRequested(
      state,
      action: PayloadAction<{ componentId: string; file: File }>,
    ) {
      state.mediaUploading = 'component-image';
      state.mediaUploadingComponentId = action.payload.componentId;
      state.error = null;
    },
    profileComponentImageRemoveRequested(state, action: PayloadAction<{ componentId: string }>) {
      state.mediaUploading = 'component-image';
      state.mediaUploadingComponentId = action.payload.componentId;
      state.error = null;
    },
    componentConfigPersistRequested(state, _action: PayloadAction<string>) {
      state.saving = true;
    },
    profileMediaUploadFinished(state) {
      state.mediaUploading = null;
      state.mediaUploadingComponentId = null;
      state.saving = false;
    },
    profileFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.saving = false;
      state.mediaUploading = null;
      state.mediaUploadingComponentId = null;
      state.error = action.payload;
    },
  },
});

export const {
  profileLoadRequested,
  profileLoaded,
  profileCreateRequested,
  profileSaveRequested,
  profilePublishRequested,
  profileUnpublishRequested,
  profileSaveSucceeded,
  profilePatch,
  profileThemePatch,
  componentsLoaded,
  profileComponentsWatchRequested,
  componentAddRequested,
  componentUpdateRequested,
  componentRemoveRequested,
  componentReorderRequested,
  componentMutationFinished,
  componentConfigPatched,
  componentConfigPersistRequested,
  componentEditStarted,
  componentEditEnded,
  profileFailed,
  profileAvatarUploadRequested,
  profileBackgroundUploadRequested,
  profileBackgroundRemoveRequested,
  profileComponentImageUploadRequested,
  profileComponentImageRemoveRequested,
  profileMediaUploadFinished,
} = profileSlice.actions;

export default profileSlice.reducer;
